/**
 * EN-837 — single source of truth for "is a recording/upload in flight?", plus a helper
 * to defer a destructive side-effect (reload, hard-nav, sign-out) until the recorder is idle.
 *
 * The MediaRecorder lives in AssetLibraryProvider inside PermissionWrapper (the authenticated
 * shell). A full-page reload, a hard window.location navigation, or clearing the Supabase
 * session (which rebuilds the router with unauthenticated routes) all tear the recorder down
 * mid-capture. Callers that might fire during a recording route through here so the destructive
 * action waits until the recording is stopped and its audio persisted/uploaded.
 *
 * Plain module (no React) so non-React callers (retryWrapper) can use it. Imports only the
 * store + types (and Sentry for breadcrumbs) to avoid circular dependencies.
 */

import * as Sentry from "@sentry/react";

import store from "@/app/store";
import { RecordingState } from "@/common/utils/types";

/**
 * Upper bound on how long a destructive action may sit deferred before the watchdog
 * intervenes. Generous on purpose: a bounded operation (doc completion, upload) that has
 * not resolved in two minutes is presumed stuck, not merely slow.
 */
const DEFAULT_MAX_DEFERRAL_MS = 120_000;

/**
 * Absolute ceiling for how long a teardown may sit deferred behind *audio in flight* (a live
 * capture OR an upload still uploading) before we escalate to a captured Sentry error. The
 * watchdog never force-ends audio in flight (that is the audio loss this module prevents), but a
 * flag stuck `recording`/`paused`/`isUploadingGlobal` this long is almost certainly a wedged flag
 * — a crashed MediaRecorder, an unmount that never dispatched `inactive`, an upload promise that
 * never settled its flag — not a genuine 20-minute recording or upload still in flight.
 * Breadcrumb-only signalling would leave that stranded user invisible in Sentry, so past this
 * ceiling we raise a real, findable issue while STILL holding (never tearing down) the operation.
 */
const DEFAULT_LIVE_CAPTURE_ESCALATION_MS = 20 * 60_000;

interface RunWhenRecorderIdleOptions {
    /** Watchdog threshold; see DEFAULT_MAX_DEFERRAL_MS. Exposed mainly for tests. */
    maxDeferralMs?: number;
    /** Live-capture escalation ceiling; see DEFAULT_LIVE_CAPTURE_ESCALATION_MS. Exposed mainly for tests. */
    liveCaptureEscalationMs?: number;
}

/** The globalState fields that determine whether the recorder is busy. */
interface RecorderRelevantState {
    globalRecordingState: RecordingState;
    isUploadingGlobal: boolean;
    completing: boolean;
    // EN-837: the stop→upload bridge (browser transport). Bounded like `completing` — NOT part of
    // `hasAudioInFlight`, so the watchdog may release it — because the audio is already persisted in
    // IndexedDB snapshots by the time we're finalizing; only the not-yet-started upload is pending.
    finalizing: boolean;
}

/**
 * A live audio capture — recording or paused. One of the two ways unpersisted audio can be in
 * flight (see `hasAudioInFlight`).
 */
function isCaptureLive(state: Pick<RecorderRelevantState, "globalRecordingState">): boolean {
    return (
        state.globalRecordingState === RecordingState.recording || state.globalRecordingState === RecordingState.paused
    );
}

/**
 * Is there UNPERSISTED AUDIO in flight — either a live capture (recording/paused) or an upload
 * still uploading (`isUploadingGlobal`)? This is the state the watchdog must NEVER force past:
 * tearing the page down mid-capture OR mid-upload is the exact audio loss EN-837 prevents. The
 * bounded flags (`completing` — document generation; `finalizing` — the stop→upload bridge) carry
 * NO unpersisted audio (it is already in IndexedDB snapshots), so they are safe to release from
 * once they exceed the deferral threshold.
 */
export function hasAudioInFlight(
    state: Pick<RecorderRelevantState, "globalRecordingState" | "isUploadingGlobal">,
): boolean {
    return isCaptureLive(state) || state.isUploadingGlobal;
}

/**
 * The single definition of "is the recorder busy?" — a recording, upload, or document-completion
 * is in progress. Pure so both the imperative isRecorderActive() (non-React callers) and reactive
 * useAppSelector-based components can share one predicate without drifting apart.
 */
export function selectRecorderActive(state: RecorderRelevantState): boolean {
    return (
        state.globalRecordingState === RecordingState.recording ||
        state.globalRecordingState === RecordingState.paused ||
        state.isUploadingGlobal ||
        state.completing ||
        state.finalizing
    );
}

/** True when a recording, upload, or document-completion is in progress. */
export function isRecorderActive(): boolean {
    return selectRecorderActive(store.getState().globalState);
}

/**
 * Run `fn` immediately if the recorder is idle; otherwise defer until it next goes idle and
 * run it exactly once. Returns a cancel function that prevents a still-pending deferred run.
 *
 * Watchdog: a recorder flag that gets stuck `true` (e.g. a `completing` flag that a page
 * failed to clear) would otherwise block the deferred action forever. To bound that, after
 * `maxDeferralMs` the deferral is released — BUT only when the hold is a bounded flag
 * (`completing` document generation, or `finalizing` the stop→upload bridge — audio already
 * persisted). While UNPERSISTED AUDIO is in flight — a live
 * recording/paused capture OR an upload still uploading (`hasAudioInFlight`) — the watchdog never
 * releases (it re-arms indefinitely and records a breadcrumb once) because tearing down either
 * mid-flight is the precise audio loss this whole module exists to prevent. If an audio-in-flight
 * hold persists past `liveCaptureEscalationMs` (a wedged flag, not a real recording/upload), we
 * still never tear it down — but we escalate to a captured Sentry error so the stranded user is
 * visible.
 */
export function runWhenRecorderIdle(fn: () => void, options: RunWhenRecorderIdleOptions = {}): () => void {
    if (!isRecorderActive()) {
        fn();
        return () => {};
    }

    const maxDeferralMs = options.maxDeferralMs ?? DEFAULT_MAX_DEFERRAL_MS;
    const liveCaptureEscalationMs = options.liveCaptureEscalationMs ?? DEFAULT_LIVE_CAPTURE_ESCALATION_MS;
    let done = false;
    let timer: ReturnType<typeof setTimeout>;
    let warnedAudioInFlight = false;
    let escalatedAudioInFlight = false;
    let audioInFlightDeferralMs = 0;

    const finish = () => {
        if (done) return;
        done = true;
        unsubscribe();
        clearTimeout(timer);
        fn();
    };

    const onWatchdog = () => {
        if (done) return;
        const globalState = store.getState().globalState;
        if (hasAudioInFlight(globalState)) {
            // Unpersisted audio is in flight — a live capture OR an upload still uploading. Never
            // tear it down; that is the exact audio loss EN-837 prevents. Keep watching, note once.
            if (!warnedAudioInFlight) {
                warnedAudioInFlight = true;
                Sentry.addBreadcrumb({
                    category: "recorder-activity",
                    level: "warning",
                    message: "Teardown deferred past threshold while audio is in flight; holding (not forcing).",
                    data: { maxDeferralMs },
                });
            }
            audioInFlightDeferralMs += maxDeferralMs;
            if (!escalatedAudioInFlight && audioInFlightDeferralMs >= liveCaptureEscalationMs) {
                // Held against in-flight audio this long is almost certainly a wedged flag, not a
                // genuine long recording/upload. Surface a real, findable Sentry issue (breadcrumbs
                // alone are invisible unless a later error fires) — but STILL hold, never tear down.
                escalatedAudioInFlight = true;
                Sentry.captureException(
                    new Error(
                        "recorderIdleWatchdog: teardown stranded — recorder flag stuck with audio in flight past escalation ceiling",
                    ),
                    {
                        tags: { errorCategory: "recording", operation: "recorderIdleWatchdog" },
                        extra: {
                            audioInFlightDeferralMs,
                            maxDeferralMs,
                            liveCaptureEscalationMs,
                            globalRecordingState: globalState.globalRecordingState,
                            isUploadingGlobal: globalState.isUploadingGlobal,
                        },
                    },
                );
            }
            timer = setTimeout(onWatchdog, maxDeferralMs);
            return;
        }
        // Held only by a bounded flag (`completing` — document generation; or `finalizing` — the
        // stop→upload bridge) past the threshold. The audio is already persisted, nothing is at risk,
        // so a flag stuck this long is presumed wedged → release the deferred teardown. A breadcrumb
        // is enough; unlike the audio-in-flight branch this can never destroy data.
        Sentry.addBreadcrumb({
            category: "recorder-activity",
            level: "warning",
            message:
                "Recorder-idle watchdog released a stuck deferred teardown (completing/finalizing flag exceeded threshold).",
            data: { maxDeferralMs, globalState },
        });
        finish();
    };

    let wasCaptureLive = isCaptureLive(store.getState().globalState);
    let wasUploading = store.getState().globalState.isUploadingGlobal;

    const unsubscribe = store.subscribe(() => {
        if (done) return;
        const globalState = store.getState().globalState;
        if (!selectRecorderActive(globalState)) {
            // Fully idle — run the deferred action now.
            finish();
            return;
        }
        // Still busy. Restart the grace window whenever a fresh audio-in-flight operation begins, so
        // it gets its OWN full window instead of inheriting time already charged to a prior phase:
        //   (a) a live capture just ended but a bounded/upload flag remains, or
        //   (b) a new upload just started (isUploadingGlobal false -> true).
        // Without (b), time already spent holding for a live capture or a `completing` flag is charged
        // against a freshly-started upload, tripping its escalation ceiling early. Edge-triggered on
        // the transition — NOT re-armed on every store tick.
        const nowCaptureLive = isCaptureLive(globalState);
        const nowUploading = globalState.isUploadingGlobal;
        const captureJustEnded = wasCaptureLive && !nowCaptureLive;
        const uploadJustStarted = !wasUploading && nowUploading;
        if (captureJustEnded || uploadJustStarted) {
            clearTimeout(timer);
            audioInFlightDeferralMs = 0;
            timer = setTimeout(onWatchdog, maxDeferralMs);
        }
        wasCaptureLive = nowCaptureLive;
        wasUploading = nowUploading;
    });

    timer = setTimeout(onWatchdog, maxDeferralMs);

    return () => {
        done = true;
        unsubscribe();
        clearTimeout(timer);
    };
}

/**
 * The one capability the coalescing runner needs from `runWhenRecorderIdle`: defer `fn` until idle
 * and hand back a cancel handle. Named separately (rather than `typeof runWhenRecorderIdle`) so the
 * factory's public signature exposes only what it consumes — not the options param it never passes —
 * and so a test fake is trivial to type.
 */
export type DeferUntilIdle = (fn: () => void) => () => void;

/** A schedulable, coalescing wrapper around `runWhenRecorderIdle`. See `createCoalescingIdleRunner`. */
export interface CoalescingIdleRunner {
    /**
     * Defer `fn` until the recorder is idle, superseding any run this runner still has pending.
     * Runs immediately (and clears any prior pending run) when the recorder is already idle.
     */
    schedule: (fn: () => void) => void;
    /** Cancel a still-pending deferred run, if any. Safe to call when nothing is pending. */
    cancel: () => void;
}

/**
 * EN-837 — a single-slot, coalescing scheduler over `runWhenRecorderIdle`.
 *
 * `runWhenRecorderIdle` subscribes to the store while a recording is live and fires its callback
 * once the recorder goes idle. A caller that can re-fire while a recording is in flight — e.g.
 * AccountProvider's account-data (re)fetch, which re-runs on every session-object identity change
 * (token refresh) — would otherwise stack a fresh subscription + deferred teardown on each call,
 * so N failed fetches during one recording fire N sign-outs (and leak N-1 subscriptions) when the
 * recorder finally goes idle. This runner holds at most ONE pending deferral: each `schedule`
 * cancels the previous one first. Cancel on unmount, or when the condition that triggered the
 * deferral no longer holds, to avoid a stale teardown firing later.
 *
 * `run` is injectable purely so the coalescing behaviour can be unit-tested without the store.
 */
export function createCoalescingIdleRunner(run: DeferUntilIdle = runWhenRecorderIdle): CoalescingIdleRunner {
    let cancelPending: (() => void) | null = null;
    const cancel = () => {
        cancelPending?.();
        cancelPending = null;
    };
    return {
        schedule(fn: () => void) {
            cancel();
            // When the recorder is already idle, `run` fires `fn` SYNCHRONOUSLY (the callback below
            // nulls `cancelPending`) and returns a no-op cancel. Assigning that return unconditionally
            // would overwrite the null and leave a stale non-null handle, violating the invariant
            // "cancelPending is null when nothing is pending" (EN-837 finding #4). Only retain the
            // handle when the run was genuinely deferred, so a later truthiness check can't misread a
            // synchronous, already-finished run as still pending.
            let ranSync = false;
            const cancelHandle = run(() => {
                ranSync = true;
                cancelPending = null;
                fn();
            });
            if (!ranSync) cancelPending = cancelHandle;
        },
        cancel,
    };
}
